import {
	PreviewSlide,
	ReportDateRange
} from '../models/preview/preview.models';
import {
	ReportContentItem,
	ReportSettingsModel
} from './report-settings.service';
import {
	SlideEditOverride,
	applyOverrides
} from '../models/preview/preview-overrides.models';
import {
	ReportCustomDays,
	customDaysOf,
	utcDayEnd,
	utcDayStart
} from './report-period';
import { reportDateLocale, reportMessage } from './report-messages';

// Slides temporarily hidden from BOTH the in-app preview and the PDF export
// because the backend data they render isn't available yet. This is the single
// switch that gates visibility on top of each item's `enabled` flag — remove
// the entry to re-enable a slide once its data lands. Tracked in T-352.
//   - website_traffic_demographics ("Audience Demographics"): needs per-age-
//     cohort audience metrics (sessions / avg. time / bounce), not yet
//     populated, so the donut + table render empty ("—").
export const TEMPORARILY_HIDDEN_SLIDE_IDS: ReadonlySet<string> = new Set([
	'website_traffic_demographics'
]);

export function isSlideTemporarilyHidden(slideId: string): boolean {
	return TEMPORARILY_HIDDEN_SLIDE_IDS.has(slideId);
}

/**
 * The sections a finished report shows, in order: its cover, then every section
 * still switched on.
 *
 * The one list both the PDF export and the view-only share link render, so the
 * file somebody downloads and the link somebody is sent cannot disagree about
 * what the report contains.
 */
export function reportDocumentItems(
	settings: ReportSettingsModel
): ReportContentItem[] {
	const coverItem: ReportContentItem = {
		id: 'cover_page',
		name: 'Cover Page',
		enabled: true,
		title: settings.coverTitle,
		description: settings.coverDescription,
		widgetOverrides: {}
	};
	return [
		coverItem,
		...settings.contentItems.filter(
			item => item.enabled && !isSlideTemporarilyHidden(item.id)
		)
	];
}

// Platform / channel id → human display name with correct brand casing.
// Anything not mapped falls back to capitalizing the first letter.
const PLATFORM_DISPLAY_NAMES: Record<string, string> = {
	x: 'X',
	twitter: 'X',
	'x(twitter)': 'X',
	reddit: 'Reddit',
	linkedin: 'LinkedIn',
	tiktok: 'TikTok',
	youtube: 'YouTube',
	facebook: 'Facebook',
	instagram: 'Instagram',
	news: 'News',
	blogs: 'Blogs',
	forums: 'Forums',
	web: 'Web'
};

export function platformDisplayName(id: string): string {
	const raw = (id ?? '').trim();
	if (!raw) return raw;
	const mapped = PLATFORM_DISPLAY_NAMES[raw.toLowerCase()];
	if (mapped) return mapped;
	return raw.charAt(0).toUpperCase() + raw.slice(1);
}

// Language code (ISO 639, e.g. "en") → full English name ("English"), title-
// cased. Uses Intl.DisplayNames; unknown/private codes (e.g. "qme") fall back
// to the uppercased code.
let _languageDisplayNames: Intl.DisplayNames | null = null;
export function languageDisplayName(code: string): string {
	const raw = (code ?? '').trim();
	if (!raw) return raw;
	try {
		if (!_languageDisplayNames) {
			_languageDisplayNames = new Intl.DisplayNames(['en'], {
				type: 'language'
			});
		}
		const name = _languageDisplayNames.of(raw.toLowerCase());
		if (name && name.toLowerCase() !== raw.toLowerCase()) {
			return name.charAt(0).toUpperCase() + name.slice(1);
		}
	} catch {
		// Intl.DisplayNames unavailable or rejected the code — fall through.
	}
	return raw.toUpperCase();
}

function formatDate(date: Date, language: string | null | undefined): string {
	return new Intl.DateTimeFormat(reportDateLocale(language), {
		month: 'short',
		day: '2-digit',
		year: 'numeric',
		timeZone: 'UTC'
	}).format(date);
}

/**
 * Where an "all time" report's window starts: before any post the product
 * could have collected, so the preview reads everything, as the server's plan
 * does (`report_period_window` gives `all_time` no lower bound at all). A
 * fixed instant rather than 1970, which the ClickHouse driver refuses as a
 * bogus None.
 */
const ALL_TIME_START = '2000-01-01T00:00:00.000Z';

function shiftDate(date: Date, period: string): Date {
	const shiftedDate = new Date(date.toISOString());

	switch (period) {
		case 'last_7_days':
			shiftedDate.setUTCDate(shiftedDate.getUTCDate() - 6);
			break;
		case 'last_30_days':
			shiftedDate.setUTCDate(shiftedDate.getUTCDate() - 29);
			break;
		case 'last_3_months':
			shiftedDate.setUTCMonth(shiftedDate.getUTCMonth() - 3);
			break;
		case 'last_6_months':
			shiftedDate.setUTCMonth(shiftedDate.getUTCMonth() - 6);
			break;
		case 'last_year':
			shiftedDate.setUTCFullYear(shiftedDate.getUTCFullYear() - 1);
			break;
		// It fell through to the default and came back unchanged, so an
		// "all time" report asked for the window from now to now and every
		// slide drew nothing — while the plan it was made from had counted
		// everything.
		case 'all_time':
			return new Date(ALL_TIME_START);
		default:
			break;
	}

	return shiftedDate;
}

export function buildPreviewDateRange(
	period: string,
	generatedAt: string = new Date().toISOString(),
	customDays: ReportCustomDays | null = null,
	language?: string | null
): ReportDateRange {
	const endDate = new Date(
		customDays ? utcDayEnd(customDays.to) : generatedAt
	);
	const startDate = customDays
		? new Date(utcDayStart(customDays.from))
		: shiftDate(endDate, period);

	return {
		period,
		start: startDate.toISOString(),
		end: endDate.toISOString(),
		// A range starting in 2000 is the mechanism, not the meaning.
		label:
			period === 'all_time'
				? reportMessage(language, 'window.all_time')
				: `${formatDate(startDate, language)} - ${formatDate(
						endDate,
						language
				  )}`
	};
}

export function reportDateRange(
	settings: Pick<
		ReportSettingsModel,
		'period' | 'periodFrom' | 'periodTo' | 'language'
	>,
	generatedAt?: string
): ReportDateRange {
	return buildPreviewDateRange(
		settings.period,
		generatedAt,
		customDaysOf(settings),
		settings.language
	);
}

/**
 * Replay a slide's saved edits onto a freshly built slide, and paint it in the
 * report's own colour.
 *
 * The theme is expressed the same way a reader's recolour is — a from → to
 * substitution over the slide's widgets — because every colour a slide shows,
 * headings and KPI accents as much as chart series, is read back off the built
 * slide AFTER this runs. One substitution here reaches all of them, and no
 * slide needs to know a theme exists.
 *
 * Only the deck's default accent moves. Sentiment and emotion colours are the
 * meaning of those charts, not decoration — a green that stops meaning
 * "positive" costs the reader more than an off-brand chart does — so they stay
 * where they are, and so do the later entries of the categorical palettes,
 * which have to stay distinguishable from one another. The first entry of
 * those palettes IS the default accent, so it follows the theme on its own.
 */
export function applyContentItemOverrides(
	slide: PreviewSlide,
	item: ReportContentItem,
	themeColor?: string | null
): PreviewSlide {
	const overrides: SlideEditOverride[] = [];

	if (item.widgetOverrides) {
		Object.entries(item.widgetOverrides).forEach(
			([widgetId, widgetOverride]) => {
				if (widgetOverride.text !== undefined) {
					overrides.push({
						type: 'text',
						widgetId,
						text: widgetOverride.text,
						editedByUser: widgetOverride.editedByUser === true
					});
				}

				if (widgetOverride.chartType) {
					overrides.push({
						type: 'chart_type',
						widgetId,
						chartType: widgetOverride.chartType
					});
				}

				if (widgetOverride.visible !== undefined) {
					overrides.push({
						type: 'visibility',
						widgetId,
						visible: widgetOverride.visible
					});
				}

				if (widgetOverride.colorOverrides) {
					overrides.push(
						...Object.entries(widgetOverride.colorOverrides).map(
							([fromColor, toColor]): SlideEditOverride => ({
								type: 'color',
								widgetId,
								fromColor,
								toColor
							})
						)
					);
				}
			}
		);
	}

	// After the saved edits, so a reader who deliberately painted one widget
	// keeps that colour instead of having the theme take it back.
	const theme = normalizeThemeColor(themeColor);
	if (theme && theme !== REPORT_DEFAULT_ACCENT_COLOR) {
		for (const widget of slide.widgets) {
			overrides.push({
				type: 'color',
				widgetId: widget.id,
				fromColor: REPORT_DEFAULT_ACCENT_COLOR,
				toColor: theme
			});
		}
	}

	if (overrides.length === 0) {
		return slide;
	}

	return applyOverrides(slide, overrides);
}

/** The colour the slides are built in before any theme is applied. */
export const REPORT_DEFAULT_ACCENT_COLOR = '#2872e1';

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

/**
 * A theme colour is only ever a six-digit hex. Anything else is dropped rather
 * than passed through: the value ends up inside chart configuration and inline
 * styles, where a malformed one paints nothing and leaves no trace of why.
 */
export function normalizeThemeColor(
	value: string | null | undefined
): string | null {
	const trimmed = (value ?? '').trim().toLowerCase();
	return HEX_COLOR.test(trimmed) ? trimmed : null;
}

export function hasPositiveTotal(
	values: Array<number | null | undefined>
): boolean {
	return values.some(value => Number(value) > 0);
}

export function hasPositiveDataPointTotal(
	points: Array<{ value: number | null | undefined }>
): boolean {
	return hasPositiveTotal(points.map(point => point.value));
}

// Compact number for KPI callouts: 32300 -> "32.3K", 8_400_000 -> "8.4M".
// Matches the abbreviated figures in the report design.
export function formatCompactNumber(value: number): string {
	const stripZero = (n: number) => n.toFixed(1).replace(/\.0$/, '');
	if (value >= 1_000_000) return `${stripZero(value / 1_000_000)}M`;
	if (value >= 1_000) return `${stripZero(value / 1_000)}K`;
	return String(value);
}
